/**
 * WeDoScreen - K Comparison We Do Practice
 *
 * Guided practice where user selects the greater number with hints available.
 * Plays "Which is greater? Tap the bigger number." then highlights correct answer.
 * Wrong answers show the correct answer and play "[X] is greater."
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { ComparisonFact } from './types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';

// Use PreK shared components
import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

interface WeDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: ComparisonFact[];
  onComplete: () => void;
  onBack: () => void;
}

const WeDoScreen: React.FC<WeDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [failedFacts, setFailedFacts] = useState<string[]>([]);
  const [isRetryingFailed, setIsRetryingFailed] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [highlightCorrect, setHighlightCorrect] = useState(false);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playChime, playTTSWithCallback, isTTSPlaying } = useKTTS();

  // Get result audio file for a given number
  const getResultAudio = useCallback((result: number) => {
    const resultAudio = K_QUESTION_SCREEN_AUDIO.comparing.iDoResult;
    return resultAudio[result as keyof typeof resultAudio];
  }, []);

  // Get current facts list (original or failed facts for retry)
  const getCurrentFacts = useCallback((): ComparisonFact[] => {
    if (isRetryingFailed && failedFacts.length > 0) {
      return factsToPractice.filter((f) => failedFacts.includes(f.factId));
    }
    return factsToPractice;
  }, [factsToPractice, failedFacts, isRetryingFailed]);

  const currentFacts = getCurrentFacts();
  const currentFact = currentFacts[currentFactIndex];

  // Reset state when fact changes
  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setHasAnswered(false);
    setWrongAnswer(null);
    setWaitingForCorrectAnswer(false);
    setTtsHasPlayed(false);
    setHighlightCorrect(false);
  }, [currentFactIndex, isRetryingFailed]);

  // Play TTS instruction when question loads (like NumberNaming WeDoScreen)
  useEffect(() => {
    if (!currentFact || ttsHasPlayed) return;

    if (settings.speechEnabled) {
      // Play "Which is greater? Tap the bigger number."
      playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.weDoIntro, () => {
        setTtsHasPlayed(true);
        // Show correct answer highlight briefly after TTS
        const timeout1 = setTimeout(() => {
          setHighlightCorrect(true);
          const timeout2 = setTimeout(() => setHighlightCorrect(false), 2000);
          timeoutsRef.current.push(timeout2);
        }, 500);
        timeoutsRef.current.push(timeout1);
      }).catch(() => {
        setTtsHasPlayed(true);
        // Still show highlight even if TTS fails
        const timeout1 = setTimeout(() => {
          setHighlightCorrect(true);
          const timeout2 = setTimeout(() => setHighlightCorrect(false), 2000);
          timeoutsRef.current.push(timeout2);
        }, 500);
        timeoutsRef.current.push(timeout1);
      });
    } else {
      // If speech disabled, still show the highlight
      setTtsHasPlayed(true);
      const timeout1 = setTimeout(() => {
        setHighlightCorrect(true);
        const timeout2 = setTimeout(() => setHighlightCorrect(false), 2000);
        timeoutsRef.current.push(timeout2);
      }, 1000);
      timeoutsRef.current.push(timeout1);
    }
  }, [currentFact, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const advanceToNextFact = useCallback(() => {
    if (currentFactIndex < currentFacts.length - 1) {
      setCurrentFactIndex(currentFactIndex + 1);
    } else {
      // Completed current sequence
      if (!isRetryingFailed && failedFacts.length > 0) {
        // Retry failed facts
        setIsRetryingFailed(true);
        setCurrentFactIndex(0);
      } else {
        // All done
        onComplete();
      }
    }
  }, [currentFactIndex, currentFacts.length, failedFacts.length, isRetryingFailed, onComplete]);

  const handleAnswerClick = useCallback(
    (selectedNumber: number) => {
      if (!currentFact) return;

      // Don't allow clicks while TTS is playing
      if (isTTSPlaying) return;

      // If waiting for correct answer, only allow correct selection
      if (waitingForCorrectAnswer && selectedNumber !== currentFact.result) return;

      // If already answered correctly, don't allow more clicks
      if (hasAnswered && !waitingForCorrectAnswer) return;

      const isCorrect = selectedNumber === currentFact.result;

      if (isCorrect) {
        setHasAnswered(true);
        setWaitingForCorrectAnswer(false);

        // Remove from failed facts if it was there
        setFailedFacts((prev) => prev.filter((id) => id !== currentFact.factId));

        playChime().then(() => {
          const timeout = setTimeout(() => {
            advanceToNextFact();
          }, 500);
          timeoutsRef.current.push(timeout);
        });
      } else {
        setWrongAnswer(selectedNumber);

        // Add to failed facts
        if (!failedFacts.includes(currentFact.factId)) {
          setFailedFacts((prev) => [...prev, currentFact.factId]);
        }

        // Clear wrong answer flash
        const timeout1 = setTimeout(() => setWrongAnswer(null), 1000);
        timeoutsRef.current.push(timeout1);

        // Play result audio "[X] is greater." if speech enabled
        if (settings.speechEnabled) {
          const timeout2 = setTimeout(() => {
            const resultAudio = getResultAudio(currentFact.result);
            if (resultAudio) {
              playTTSWithCallback(resultAudio).catch(() => {});
            }
          }, 300);
          timeoutsRef.current.push(timeout2);
        }

        // Wait for correct answer
        setWaitingForCorrectAnswer(true);
        setHasAnswered(false);
      }
    },
    [
      currentFact,
      hasAnswered,
      waitingForCorrectAnswer,
      failedFacts,
      settings.speechEnabled,
      isTTSPlaying,
      playChime,
      playTTSWithCallback,
      getResultAudio,
      advanceToNextFact,
    ]
  );

  if (!currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.WE_DO}
        onBack={onBack}
        progress={
          currentFacts.length > 0
            ? Math.min((currentFactIndex + 1) / currentFacts.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Question prompt */}
          <div className="text-center mb-8">
            <div className="text-2xl lg:text-3xl text-gray-700 font-semibold">
              Which is greater?
            </div>
            {isRetryingFailed && (
              <div className="text-sm text-orange-500 mt-2">Let's try these again!</div>
            )}
          </div>

          {/* Two number choices stacked vertically */}
          <div className="flex flex-col gap-6">
            {[currentFact.operand1, currentFact.operand2].map((num, index) => {
              const isCorrect = num === currentFact.result;
              const isWrong = wrongAnswer === num;
              const showAsCorrect = hasAnswered && isCorrect;
              const showHighlight = (highlightCorrect || waitingForCorrectAnswer) && isCorrect;

              return (
                <motion.button
                  key={`${currentFact.factId}-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: showHighlight ? [1, 1.1, 1] : 1,
                    boxShadow: showHighlight
                      ? ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)']
                      : undefined
                  }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.1,
                    scale: { duration: showHighlight ? 2 : 0.4, repeat: showHighlight ? 1 : 0 }
                  }}
                  whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.05 } : {}}
                  whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.95 } : {}}
                  onClick={() => handleAnswerClick(num)}
                  disabled={(hasAnswered && !waitingForCorrectAnswer) || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl
                    transition-all duration-300
                    ${
                      isWrong
                        ? 'bg-red-500 text-white border-4 border-red-700 animate-pulse'
                        : showAsCorrect
                        ? 'bg-green-500 text-white border-4 border-green-700'
                        : showHighlight
                        ? 'bg-green-500 text-white border-4 border-green-700 animate-pulse shadow-green-300'
                        : hasAnswered && !isCorrect
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                        : waitingForCorrectAnswer && !isCorrect
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : isTTSPlaying
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer'
                    }
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {num}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default WeDoScreen;
